/**
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  Chip,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Person as DriversIcon,
  PersonAdd as DriverApprovalIcon,
  LocalTaxi as TripsIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Help as HelpIcon,
  ChevronLeft,
  ChevronRight,
  Map as MapIcon,
  BugReport as BugReportIcon,
  AccountBalanceWallet as CashoutIcon,
  DirectionsCar as VehicleIcon,
  Warning as SOSIcon,
  People as UsersIcon,
  PersonRemove as DeletionIcon,
  MonitorHeart as SystemStatusIcon,
  AdminPanelSettings as PortalUsersIcon,
  MenuBook as DocsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import type { AdminRole } from '../contexts/AuthContext';
import { graphqlSubscribe } from '../services/graphql';
import { useSOS } from '../contexts/SOSContext';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const { alerts: sosAlerts, unreadCount: sosUnreadCount, clearUnread: clearSosUnread } = useSOS();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasRole } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/login');
  };
  
  const toggleDrawer = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDrawerCollapsed(!drawerCollapsed);
    }
  };

  // Each item declares which roles can see it. SuperAdmin always sees everything.
  const allMenuItems: { text: string; icon: JSX.Element; path: string; roles: AdminRole[] }[] = [
    { text: 'Dashboard',       icon: <DashboardIcon />,    path: '/',               roles: ['SuperAdmin', 'Operations', 'CustomerService', 'Finance', 'TechSupport'] },
    { text: 'Drivers',         icon: <DriversIcon />,      path: '/drivers',        roles: ['SuperAdmin', 'Operations', 'CustomerService', 'TechSupport'] },
    { text: 'Driver Approval', icon: <DriverApprovalIcon />, path: '/driver-approval', roles: ['SuperAdmin', 'Operations'] },
    { text: 'Vehicle Approval',icon: <VehicleIcon />,      path: '/vehicle-approval', roles: ['SuperAdmin', 'Operations'] },
    { text: 'Driver Map',      icon: <MapIcon />,          path: '/driver-map',     roles: ['SuperAdmin', 'Operations'] },
    { text: 'Trips',           icon: <TripsIcon />,        path: '/trips',          roles: ['SuperAdmin', 'Operations', 'CustomerService', 'Finance'] },
    { text: 'Cashout',         icon: <CashoutIcon />,      path: '/cashout',        roles: ['SuperAdmin', 'Finance'] },
    { text: 'Analytics',       icon: <AnalyticsIcon />,    path: '/analytics',      roles: ['SuperAdmin', 'Operations', 'Finance'] },
    { text: 'SOS Alerts',      icon: <Badge badgeContent={sosUnreadCount || undefined} color="error"><SOSIcon /></Badge>, path: '/sos-alerts', roles: ['SuperAdmin', 'Operations', 'CustomerService'] },
    { text: 'Users',           icon: <UsersIcon />,        path: '/users',          roles: ['SuperAdmin', 'CustomerService'] },
    { text: 'Account Deletions', icon: <DeletionIcon />,    path: '/account-deletions', roles: ['SuperAdmin', 'CustomerService'] },
    { text: 'Error Logs',      icon: <BugReportIcon />,    path: '/error-logs',     roles: ['SuperAdmin', 'TechSupport'] },
  ];

  const menuItems = allMenuItems.filter(item => hasRole(...item.roles));

  const allBottomItems: { text: string; icon: JSX.Element; path: string; roles: AdminRole[] }[] = [
    { text: 'System Status', icon: <SystemStatusIcon />, path: '/status',       roles: ['SuperAdmin', 'TechSupport'] },
    { text: 'Settings',      icon: <SettingsIcon />,     path: '/settings',     roles: ['SuperAdmin', 'Operations', 'Finance'] },
    { text: 'Docs',          icon: <DocsIcon />,         path: '/docs',         roles: ['SuperAdmin', 'TechSupport'] },
    { text: 'Portal Users',  icon: <PortalUsersIcon />,  path: '/portal-users', roles: ['SuperAdmin'] },
  ];

  const bottomMenuItems = allBottomItems.filter(item => hasRole(...item.roles));
  const bottomMenuItem = bottomMenuItems[0] ?? { text: 'Settings', icon: <SettingsIcon />, path: '/settings' };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
        {!drawerCollapsed ? (
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Typography variant="h6" noWrap component="div">
              Ride Admin
            </Typography>
            {!isMobile && (
              <IconButton onClick={toggleDrawer} sx={{ color: 'white' }}>
                <ChevronLeft />
              </IconButton>
            )}
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" width="100%">
            <IconButton onClick={toggleDrawer} sx={{ color: 'white' }}>
              <ChevronRight />
            </IconButton>
          </Box>
        )}
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <Tooltip title={drawerCollapsed ? item.text : ''} placement="right">
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  minHeight: 48,
                  justifyContent: drawerCollapsed ? 'center' : 'initial',
                  px: 2.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: 'white',
                    minWidth: 0,
                    mr: drawerCollapsed ? 'auto' : 3,
                    justifyContent: 'center'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!drawerCollapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <List>
        {bottomMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <Tooltip title={drawerCollapsed ? item.text : ''} placement="right">
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  minHeight: 48,
                  justifyContent: drawerCollapsed ? 'center' : 'initial',
                  px: 2.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.15)' },
                  },
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                }}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 0, mr: drawerCollapsed ? 'auto' : 3, justifyContent: 'center' }}>
                  {item.icon}
                </ListItemIcon>
                {!drawerCollapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerCollapsed ? 64 : drawerWidth}px)` },
          ml: { md: `${drawerCollapsed ? 64 : drawerWidth}px` },
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(10px)',
          boxShadow: 'none',
          color: '#000',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={toggleDrawer}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box display="flex" alignItems="center" sx={{ flexGrow: 1 }}>
            <Typography variant="h6" noWrap component="div">
              {[...menuItems, ...bottomMenuItems].find((item) => item.path === location.pathname)?.text || 'Dashboard'}
            </Typography>
            <Chip 
              label="Live" 
              color="success" 
              size="small" 
              sx={{ ml: 2, display: { xs: 'none', sm: 'inline-flex' } }}
            />
          </Box>
          
          {/* Help */}
          <IconButton color="inherit" sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
            <HelpIcon />
          </IconButton>
          
          {/* User Menu */}
          <Box display="flex" alignItems="center" ml={1}>
            <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
              {user?.username || 'Admin'}
            </Typography>
            <IconButton color="inherit" onClick={handleMenuOpen}>
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.username?.charAt(0) || 'A'}
              </Avatar>
            </IconButton>
          </Box>
          {/* User Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Profile Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
          
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: '#283f58',
              color: 'white'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerCollapsed ? 64 : drawerWidth,
              backgroundColor: '#283f58',
              color: 'white',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { md: `calc(100% - ${drawerCollapsed ? 64 : drawerWidth}px)` },
          mt: 8,
          display: 'flex',
          justifyContent: 'center',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Box sx={{ maxWidth: '1360px', width: '100%' }}>
          {sosAlerts.length > 0 && (
            <Alert
              severity="error"
              icon={<SOSIcon />}
              sx={{ mb: 2, cursor: 'pointer', fontWeight: 700 }}
              onClick={() => { clearSosUnread(); navigate('/sos-alerts'); }}
              onClose={(e) => { e.stopPropagation(); clearSosUnread(); }}
            >
              🆘 {sosAlerts.length} SOS alert{sosAlerts.length > 1 ? 's' : ''} — {sosAlerts[0].userName || sosAlerts[0].triggeredBy} ({sosAlerts[0].userType}) triggered panic button on trip {sosAlerts[0].tripId}. Click to view.
            </Alert>
          )}
          {children}
        </Box>
      </Box>
    </Box>
  );
}
